import { supabase } from './supabase'

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing' | 'preferences'

export interface ConsentFlags {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  preferences: boolean
}

export type ConsentChoice = 'accepted' | 'rejected' | 'custom' | null

export interface ConsentState {
  choice: ConsentChoice
  flags: ConsentFlags
  updatedAt: string | null
}

const STORAGE_KEY = 'owtli_cookie_consent'

export const DEFAULT_FLAGS: ConsentFlags = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
}

export const ALL_FLAGS: ConsentFlags = {
  necessary: true,
  analytics: true,
  marketing: true,
  preferences: true,
}

export function loadConsentFromStorage(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ConsentState
  } catch {
    return null
  }
}

export function saveConsentToStorage(state: ConsentState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

export function clearConsentFromStorage(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/** Sincroniza el consentimiento con Supabase si el usuario está autenticado */
export async function syncConsentWithDB(flags: ConsentFlags): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const { data: existing } = await supabase
      .from('user_consents')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()

    if (existing) {
      await supabase
        .from('user_consents')
        .update({ consent_flags: flags, updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
    } else {
      await supabase
        .from('user_consents')
        .insert({ user_id: user.id, consent_flags: flags })
    }
  } catch (err) {
    console.error('[Consent] Error sincronizando con DB:', err)
  }
}

/** Carga el consentimiento del usuario desde Supabase (si está autenticado) */
export async function loadConsentFromDB(): Promise<ConsentFlags | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data } = await supabase
      .from('user_consents')
      .select('consent_flags')
      .eq('user_id', user.id)
      .maybeSingle()

    return data?.consent_flags ?? null
  } catch {
    return null
  }
}
